import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { interval, Subscription } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { DataService } from '../../services/data.service';
import { DialogService } from '../../services/dialog.service';
import { CleaningStatusService } from '../../services/cleaning-status.service';
import { UnifiedActivityItem } from '../../models/project/unified-activity-item.model';
import { CollectionGet } from '../../models/collection/collection-get.model';
import { ProjectGet } from '../../models/project/project-get.model';
import { ProjectBase } from '../../models/project/project-base.model';
import { CleanStatus, CleanStatusHelper } from '../../models/project/clean-status.enum';
import { NewProjectDialogComponent, NewProjectData } from '../../components/new-project-dialog/new-project-dialog.component';
import { StartProjectDialogComponent, StartProjectDialogData } from '../../components/start-project-dialog/start-project-dialog.component';
import { DataSourceGet } from '../../models/datasource/datasource-get.model';
import { RenameDialogComponent, RenameDialogData } from '../../components/rename-dialog/rename-dialog.component';
import { CleaningSnackbarComponent, CleaningSnackbarData } from '../../components/cleaning-snackbar/cleaning-snackbar.component';

type ActivityType = 'all' | 'project' | 'dataprofile' | 'anomalydetection';

interface TypeCard {
  id: ActivityType;
  label: string;
  icon: string;
  iconActive: string;
  count: number;
}

@Component({
  selector: 'app-activity',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatMenuModule,
    MatSortModule,
    MatTooltipModule,
    MatSnackBarModule
  ],
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss']
})
export class ActivityComponent implements OnInit, AfterViewInit, OnDestroy {
  // View state
  filterMode: 'type' | 'datasource' = 'type';
  selectedType: ActivityType = 'all';
  selectedDatasourceId = '';
  selectedDatasourceName = '';
  isLeftSidebarCollapsed = false;
  private pollingSubscription: Subscription | null = null;
  
  // Data
  dataSource = new MatTableDataSource<any>([]);
  collections: CollectionGet[] = [];
  searchQuery: string = '';
  currentTypeFilter: string = 'all';
  subFilter: string = 'all';
  typeCards: TypeCard[] = [
    { id: 'all', label: 'All Activity', icon: 'docs-dark-grey', iconActive: 'docs-blue', count: 0 },
    { id: 'dataprofile', label: 'Data Assessments', icon: 'chart-dark-grey', iconActive: 'chart-blue', count: 0 },
    { id: 'anomalydetection', label: 'Anomaly Detection Runs', icon: 'radar-ai-dark-grey', iconActive: 'radar-ai-blue', count: 0 },
    { id: 'project', label: 'Cleaning Workflows', icon: 'ai-scan-dark-grey', iconActive: 'ai-scan-blue', count: 0 }
  ];
  
  // Loading states
  loading = false;
  loadingSidebar = false;
  loadingData = false;
  error: string | null = null;
  
  // Column configurations for different types
  displayedColumns: string[] = ['icon', 'name', 'sourceType', 'lastActionAt', 'actions'];
  private defaultSortColumn: string = 'lastActionAt';
  private defaultSortDirection: 'asc' | 'desc' = 'desc';
  
  @ViewChild('sortTable', { static: false }) sort!: MatSort;

  constructor(
    private dataService: DataService,
    private dialogService: DialogService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private cleaningStatusService: CleaningStatusService,
    private dialog: MatDialog
  ) {
    this.setupSortingDataAccessor();
    this.updateDataSourceFilterPredicate();
  }

  ngOnInit(): void {
    // Check for query params to set up datasource filtering
    this.route.queryParams.subscribe(params => {
      if (params['datasourceId']) {
        this.selectedDatasourceId = params['datasourceId'];
        this.selectedDatasourceName = params['datasourceName'] || 'Selected Data Source';
        this.filterMode = 'datasource';
        this.selectedType = 'project'; // Show projects when filtering by datasource
      }
    });
    
    this.loadCollections();
    this.loadActivity();
  }

  ngOnDestroy(): void {
    this.pollingSubscription?.unsubscribe();
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
      this.cdr.detectChanges();
    }
  }

  setupSortingDataAccessor(): void {
    this.dataSource.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {
        case 'name':
          return (item.projectName || item.dataSourceName || item.name || '').toLowerCase();
        case 'status':
          return this.getCleanStatus(item);
        case 'dataSources':
          return item.isTokenPresent ? 1 : 0;
        case 'projectDataSource':
          return (item.dataSourceName || '').toLowerCase();
        case 'goals':
          return item.goalCount || 0;
        case 'outputs':
          return item.outputCount || 0;
        case 'createdOn':
        case 'createdAt':
          return item.createdAt ? new Date(item.createdAt).getTime() : 0;
        case 'sourceType':
          return this.getSourceTypeName(item.sourceType).toLowerCase();
        case 'dataSourceName':
          return (item.dataSourceName || '').toLowerCase();
        case 'tags':
          return (item.tags || '').toLowerCase();
        case 'projectCount':
          return item.projectCount || 0;
        case 'updatedAt':
          return item.updatedAt ? new Date(item.updatedAt).getTime() : 0;
        case 'methodKey':
          return (item.methodKey || '').toLowerCase();
        case 'lastActionAt':
          return item.lastActionAt ? new Date(item.lastActionAt).getTime() : 0;
        case 'lastRunAt':
          return item.lastRunAt ? new Date(item.lastRunAt).getTime() : 0;
        default:
          return '';
      }
    };
  }

  private applySortConfiguration(sortColumn: string, sortDirection: 'asc' | 'desc' = 'desc'): void {
    // Store the desired sort config
    this.defaultSortColumn = sortColumn;
    this.defaultSortDirection = sortDirection;
    
    // Use a slightly longer timeout to ensure DOM has fully updated
    setTimeout(() => {
      if (this.sort) {
        // Fully disconnect the sort
        this.dataSource.sort = null;
        
        // Wait a tick for Angular to process the disconnection
        setTimeout(() => {
          if (this.sort) {
            // Configure the sort
            this.sort.active = this.defaultSortColumn;
            this.sort.direction = this.defaultSortDirection;
            
            // Reconnect to dataSource
            this.dataSource.sort = this.sort;
          }
        }, 10);
      }
    }, 10);
  }

  loadCollections(): void {
    this.loadingSidebar = true;
    this.dataService.getCollectionList().subscribe({
      next: (collections) => {
        this.collections = collections;
        this.loadingSidebar = false;
      },
      error: (error) => {
        console.error('Error loading collections:', error);
        this.loadingSidebar = false;
      }
    });
  }

  loadActivity(): void {
    this.loading = true;
    this.loadingData = true;
    this.error = null;
    
    // Load data based on selected type
    switch (this.selectedType) {
      case 'all':
        this.loadAllActivity();
        break;
      case 'project':
        this.loadProjects();
        break;
      case 'dataprofile':
        this.loadDataProfiles();
        break;
      case 'anomalydetection':
        this.loadAnomalyDetectionRuns();
        break;
    }
  }

  loadAllActivity(): void {
    this.displayedColumns = ['icon', 'name', 'sourceType', 'lastActionAt', 'actions'];
    
    this.dataService.getProjectActivityList(undefined).subscribe({
      next: (items) => {
        this.dataSource.data = items;
        this.updateTypeCounts(items);
        this.loading = false;
        this.loadingData = false;
        // Apply sort configuration for All Activity view
        this.applySortConfiguration('lastActionAt', 'desc');
      },
      error: (error) => {
        this.error = error.message;
        this.snackBar.open(error.message || 'Failed to load activity', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.loading = false;
        this.loadingData = false;
        console.error('Error loading activity:', error);
      }
    });
  }

  loadProjects(): void {
    // Use project-specific columns
    this.displayedColumns = ['name', 'status', 'goals', 'outputs', 'updatedAt', 'lastRunAt', 'actions'];
    
    // When filtering by datasource, use datasource-specific endpoint
    if (this.filterMode === 'datasource' && this.selectedDatasourceId) {
      this.dataService.getProjectListByDatasource(this.selectedDatasourceId).subscribe({
        next: (projects) => {
          this.dataSource.data = projects;
          this.loading = false;
          this.loadingData = false;
          // Apply sort configuration for Projects view
          this.applySortConfiguration('updatedAt', 'desc');
        },
        error: (error) => {
          this.error = error.message;
          this.snackBar.open(error.message || 'Failed to load projects by data source', '×', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
          this.loading = false;
          this.loadingData = false;
          console.error('Error loading projects by datasource:', error);
        }
      });
      return;
    }
    
    this.dataService.getProjectList(undefined).subscribe({
      next: (projects) => {
        this.dataSource.data = projects;
        this.updateIndividualCounts(projects, 3); // Update count for Cleaning Workflows
        this.loading = false;
        this.loadingData = false;
        // Apply sort configuration for Projects view
        this.applySortConfiguration('updatedAt', 'desc');
      },
      error: (error) => {
        this.error = error.message;
        this.snackBar.open(error.message || 'Failed to load projects', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.loading = false;
        this.loadingData = false;
        console.error('Error loading projects:', error);
      }
    });
  }

  loadDataProfiles(): void {
    // Use data profile-specific columns
    this.displayedColumns = ['runName', 'dataSourceName', 'createdOn', 'actions'];
    
    this.dataService.getDataProfileList().subscribe({
      next: (profiles) => {
        this.dataSource.data = profiles;
        this.updateIndividualCounts(profiles, 1); // Update count for Data Assessments
        this.loading = false;
        this.loadingData = false;
        // Apply sort configuration for Data Profiles view
        this.applySortConfiguration('createdOn', 'desc');
      },
      error: (error) => {
        this.error = error.message;
        this.snackBar.open(error.message || 'Failed to load data assessments', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.loading = false;
        this.loadingData = false;
        console.error('Error loading data assessments:', error);
      }
    });
  }

  loadAnomalyDetectionRuns(): void {
    // Use anomaly detection-specific columns
    this.displayedColumns = ['runName', 'methodKey', 'dataSourceName', 'createdOn', 'actions'];
    
    this.dataService.getAnomalyDetectionRunList().subscribe({
      next: (runs) => {
        this.dataSource.data = runs;
        this.updateIndividualCounts(runs, 2); // Update count for Anomaly Detection
        this.loading = false;
        this.loadingData = false;
        // Apply sort configuration for Anomaly Detection view
        this.applySortConfiguration('createdOn', 'desc');
      },
      error: (error) => {
        this.error = error.message;
        this.snackBar.open(error.message || 'Failed to load anomaly detection runs', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.loading = false;
        this.loadingData = false;
        console.error('Error loading anomaly detection runs:', error);
      }
    });
  }

  updateTypeCounts(items: UnifiedActivityItem[]): void {
    this.typeCards[0].count = items.length; // All
    this.typeCards[1].count = items.filter(i => this.isDataProfile(i)).length; // Data Assessments
    this.typeCards[2].count = items.filter(i => this.isAnomalyDetection(i)).length; // Anomaly Detection
    this.typeCards[3].count = items.filter(i => this.isProject(i)).length; // Cleaning Workflows
  }

  updateIndividualCounts(items: any[], index: number): void {
    this.typeCards[index].count = items.length;
  }

  onTypeSelect(type: ActivityType): void {
    this.selectedType = type;
    this.currentTypeFilter = type;
    this.subFilter = 'all'; // Reset sub-filter when switching top-level type
    this.updateDataSourceFilterPredicate();
    this.dataSource.filter = this.buildFilterString();

    this.loadActivity();
  }

  clearDatasourceFilter(): void {
    this.selectedDatasourceId = '';
    this.selectedDatasourceName = '';
    this.filterMode = 'type';
    this.selectedType = 'all';
    // Clear query params
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {}
    });
    this.loadActivity();
  }

  navigateToDataSources(): void {
    this.router.navigate(['/data-sources']);
  }

  getPageTitle(): string {
    // If filtering by datasource, show datasource name with project count
    if (this.filterMode === 'datasource') {
      return `Cleaning Workflows - ${this.selectedDatasourceName}`;
    }
    
    // Show the type label
    const selectedCard = this.typeCards.find(card => card.id === this.selectedType);
    return selectedCard ? selectedCard.label : 'Activity';
  }

  getCleanStatus(project: ProjectGet): string {
    if (CleanStatusHelper.isProjectIncomplete(project)) {
      return CleanStatusHelper.getLabel(CleanStatus.Incomplete);
    }
    return CleanStatusHelper.getLabel(project.cleanStatusId || CleanStatus.Incomplete);
  }

  getStatusClass(project: ProjectGet): string {
    if (CleanStatusHelper.isProjectIncomplete(project)) {
      return CleanStatusHelper.getStatusClass(CleanStatus.Incomplete);
    }
    return CleanStatusHelper.getStatusClass(project.cleanStatusId || CleanStatus.Incomplete);
  }

  getStatusIcon(project: ProjectGet): string {
    let status: CleanStatus;
    if (CleanStatusHelper.isProjectIncomplete(project)) {
      status = CleanStatus.Incomplete;
    } else {
      status = project.cleanStatusId || CleanStatus.Incomplete;
    }
    
    // Return appropriate Material icon based on status
    switch (status) {
      case CleanStatus.NotStarted:
        return 'play_circle';
      case CleanStatus.InProgress:
        return 'hourglass_empty';
      case CleanStatus.Successful:
        return 'check_circle_black';
      case CleanStatus.Failed:
        return 'error';
      case CleanStatus.Incomplete:
        return 'info';
      default:
        return 'help';
    }
  }

  getFilterStatusIcon(statusId: string): string {
    const numericId = statusId === '99' ? 99 : parseInt(statusId, 10);
    return CleanStatusHelper.getStatusIcon(numericId);
  }

  getFilterStatusClass(statusId: string): string {
    const numericId = statusId === '99' ? 99 : parseInt(statusId, 10);
    return CleanStatusHelper.getStatusClass(numericId);
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) {
      return '-';
    }
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      
      if (!dateObj || isNaN(dateObj.getTime())) {
        return 'Invalid Date';
      }
      
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}\n${hours}:${minutes}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  }

  getSourceTypeName(sourceType: string): string {
    if (!sourceType) {
      return 'Unknown';
    }
    
    const type = sourceType.toLowerCase();
    
    if (type.includes('project')) {
      return 'Cleaning Workflow';
    } else if (type.includes('datasource') || type.includes('data source')) {
      return 'Data Source';
    } else if (type.includes('dataprofile') || type.includes('data profile')) {
      return 'Data Assessment';
    } else if (type.includes('anomaly') || type.includes('detection')) {
      return 'Anomaly Detection Run';
    }
    
    return sourceType; // Return original if no match found
  }

  getActivityIcon(item: UnifiedActivityItem): string {
    const type = item.sourceType.toLowerCase();
    
    if (type.includes('project')) {
      return 'ai-scan-grey';
    } else if (type.includes('datasource') || type.includes('data source')) {
      return 'data-set-grey';
    } else if (type.includes('dataprofile') || type.includes('data profile')) {
      return 'chart-grey';
    } else if (type.includes('anomaly') || type.includes('detection')) {
      return 'radar-ai-grey';
    }
    
    return 'data-set-grey'; // Default
  }

  isProject(item: any): boolean {
    if ('sourceType' in item) {
      const type = item.sourceType.toLowerCase();
      return type.includes('project');
    }
    // If it's a ProjectGet object, it's a project
    return 'projectId' in item;
  }

  isAnomalyDetection(item: any): boolean {
    // Check for AnomalyDetectionRunListItem properties
    if ('anomalyDetectionRunId' in item || 'methodKey' in item) {
      return true;
    }
    if ('sourceType' in item) {
      const type = item.sourceType.toLowerCase();
      return type.includes('anomaly') || type.includes('detection');
    }
    return false;
  }

  isDataProfile(item: any): boolean {
    // Check for DataProfileListItem properties
    if ('dataProfileRunId' in item && 'profileJson' in item) {
      return true;
    }
    if ('sourceType' in item) {
      const type = item.sourceType.toLowerCase();
      return type.includes('dataprofile') || type.includes('data profile');
    }
    return false;
  }

  onRowClick(item: any): void {
    // Only navigate on row click for projects
    if (this.isProject(item)) {
      const id = item.primaryKey || item.projectId;
      if (id) {
        this.router.navigate(['/project-canvas', id]);
      }
    }
  }

  onOpenCanvas(event: Event, item: any): void {
    event.stopPropagation();
    const id = item.primaryKey || item.projectId;
    if (id) {
      this.router.navigate(['/project-canvas', id]);
    }
  }

  onViewOutputs(event: Event, item: any): void {
    event.stopPropagation();
    const projectId = item.primaryKey || item.projectId;
    if (!projectId) {
      return;
    }

    // If we're in All Activity view and don't have complete project data, fetch it first
    if (this.selectedType === 'all' && (item.cleanStatusId === undefined || item.goalCount === undefined)) {
      this.dataService.getProject(projectId).subscribe({
        next: (fullProject: ProjectGet) => {
          // Check if the project can view outputs
          if (!this.canViewOutputs(fullProject)) {
            this.showViewOutputsError(fullProject);
            return;
          }
          // Navigate to outputs page
          this.router.navigate(['/outputs', projectId]);
        },
        error: (error) => {
          console.error('Error fetching project details:', error);
          this.snackBar.open(error.message || 'Failed to load project details', '×', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      });
      return;
    }

    // Check validation for projects with complete data
    if (!this.canViewOutputs(item)) {
      this.showViewOutputsError(item);
      return;
    }

    // Navigate to outputs page
    this.router.navigate(['/outputs', projectId]);
  }

  /**
   * Show appropriate error message when outputs cannot be viewed
   */
  private showViewOutputsError(project: any): void {
    let errorMessage = '';
    
    // Get the effective status
    let status: CleanStatus;
    if (CleanStatusHelper.isProjectIncomplete(project)) {
      status = CleanStatus.Incomplete;
      errorMessage = 'This workflow has not been run yet. Run the workflow to generate outputs.';
    } else {
      status = project.cleanStatusId || CleanStatus.Incomplete;
      
      if (status === CleanStatus.NotStarted) {
        errorMessage = 'This workflow has not been run yet. Run the workflow to generate outputs.';
      } else if (status === CleanStatus.InProgress) {
        errorMessage = 'This workflow is currently running. Please wait for it to complete before viewing outputs.';
      } else {
        errorMessage = 'No outputs are available for this workflow.';
      }
    }

    this.snackBar.open(errorMessage, '×', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }

  onViewDataSource(event: Event, item: UnifiedActivityItem): void {
    event.stopPropagation();
    // TODO: Navigate to data source detail when actions are defined
    console.log('View data source:', item);
  }

  onViewAnomalyDetection(event: Event, item: any): void {
    event.stopPropagation();
    const id = item.primaryKey || item.anomalyDetectionRunId;
    if (id) {
      this.router.navigate(['/anomaly-detection/run', id]);
    }
  }

  onViewDataProfile(event: Event, item: any): void {
    event.stopPropagation();
    // Navigate to the profile view using the existing profile (query param distinguishes from generate mode)
    const id = item.primaryKey || item.dataProfileRunId;
    const name = item.dataSourceName || item.name;
    if (id) {
      this.router.navigate(['/data/profile', id], { queryParams: { view: 'existing', name } });
    }
  }

  onRenameAnomalyDetection(event: Event, item: any): void {
    event.stopPropagation();
    const runId = item.primaryKey || item.anomalyDetectionRunId;
    const currentName = item.name || '';
    
    if (!runId) return;

    const dialogRef = this.dialog.open(RenameDialogComponent, {
      width: '520px',
      data: {
        currentName: currentName,
        itemType: 'anomaly detection run'
      } as RenameDialogData
    });

    dialogRef.afterClosed().subscribe((newName: string | undefined) => {
      if (newName && newName !== currentName) {
        this.dataService.renameAnomalyDetectionRun(runId, newName).subscribe({
          next: (success: boolean) => {
            if (success) {
              // Update the item in the data source
              item.name = newName;
              // Trigger change detection
              this.dataSource.data = [...this.dataSource.data];
            }
          },
          error: (error) => {
            console.error('Error renaming anomaly detection run:', error);
            this.snackBar.open(error.message || 'Failed to rename run', '×', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
          }
        });
      }
    });
  }

  onRenameDataProfile(event: Event, item: any): void {
    event.stopPropagation();
    const runId = item.primaryKey || item.dataProfileRunId;
    const currentName = item.name || '';
    
    if (!runId) return;

    const dialogRef = this.dialog.open(RenameDialogComponent, {
      width: '520px',
      data: {
        currentName: currentName,
        itemType: 'data assessment'
      } as RenameDialogData
    });

    dialogRef.afterClosed().subscribe((newName: string | undefined) => {
      if (newName && newName !== currentName) {
        this.dataService.renameDataProfileRun(runId, newName).subscribe({
          next: (success: boolean) => {
            if (success) {
              // Update the item in the data source
              item.name = newName;
              // Trigger change detection
              this.dataSource.data = [...this.dataSource.data];
            }
          },
          error: (error) => {
            console.error('Error renaming data profile:', error);
            this.snackBar.open(error.message || 'Failed to rename profile', '×', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
          }
        });
      }
    });
  }

  onEditProject(event: Event, project: any): void {
    event.stopPropagation();
    
    const projectId = project.projectId || project.primaryKey;
    
    // Fetch full project details before opening the dialog
    this.dataService.getProject(projectId).subscribe({
      next: (fullProject: ProjectGet) => {
        const dialogRef = this.dialogService.openDialog(NewProjectDialogComponent, {
          width: '600px',
          data: { 
            name: fullProject.projectName, 
            description: fullProject.description || '', 
            tags: '',
            collection: fullProject.collectionId,
            availableCollections: this.collections.map(c => ({
              id: c.collectionId || '',
              name: c.collectionName
            })),
            isEdit: true,
            projectId: projectId
          } as NewProjectData & { isEdit: boolean; projectId: string }
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result && result.data) {
            this.updateProject(result.data, projectId);
          }
        });
      },
      error: (error) => {
        console.error('Error fetching project details:', error);
        this.snackBar.open(error.message || 'Failed to load project details', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  updateProject(data: NewProjectData, projectId: string): void {
    const projectData: ProjectBase = {
      projectId: projectId,
      projectName: data.name,
      description: data.description || ''
    };

    this.dataService.updateProject(projectData).subscribe({
      next: (success: boolean) => {
        if (success) {
          console.log('Project updated successfully');
          this.loadActivity();
        } else {
          console.error('Failed to update project');
          this.snackBar.open('Failed to update project. Please try again.', '×', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      },
      error: (error) => {
        console.error('Error updating project:', error);
        this.snackBar.open('Failed to update project. Please try again.', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  onDeleteProject(event: Event, project: any): void {
    event.stopPropagation();
    
    this.dialogService.openConfirmDialog(
      'Delete Project',
      `Are you sure you want to delete "${project.projectName}"? This action cannot be undone.`,
      'Delete',
      'Cancel'
    ).subscribe(confirmed => {
      if (confirmed) {
        this.deleteProject(project.projectId || project.primaryKey);
      }
    });
  }

  deleteProject(projectId: string): void {
    this.dataService.deleteProject(projectId).subscribe({
      next: (success: boolean) => {
        if (success) {
          console.log('Project deleted successfully');
          this.loadActivity();
        } else {
          console.error('Failed to delete project');
          this.snackBar.open('Failed to delete project. Please try again.', '×', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      },
      error: (error) => {
        console.error('Error deleting project:', error);
        this.snackBar.open('Failed to delete project. Please try again.', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  onDeleteAnomalyDetection(event: Event, item: any): void {
    event.stopPropagation();
    const runId = item.anomalyDetectionRunId || item.primaryKey;
    const name = item.name || item.methodKey || 'Anomaly Detection Run';

    this.dialogService.openConfirmDialog(
      'Delete Anomaly Detection Run',
      `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      'Delete',
      'Cancel'
    ).subscribe(confirmed => {
      if (confirmed && runId) {
        this.deleteAnomalyDetection(runId);
      }
    });
  }

  deleteAnomalyDetection(runId: string): void {
    this.dataService.deleteAnomalyDetectionRun(runId).subscribe({
      next: (success: boolean) => {
        if (success) {
          console.log('Anomaly detection run deleted successfully');
          this.loadActivity();
        } else {
          console.error('Failed to delete anomaly detection run');
          this.snackBar.open('Failed to delete run. Please try again.', '×', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      },
      error: (error) => {
        console.error('Error deleting anomaly detection run:', error);
        this.snackBar.open('Failed to delete run. Please try again.', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  onDeleteDataProfile(event: Event, item: any): void {
    event.stopPropagation();
    const runId = item.dataProfileRunId || item.primaryKey;
    const name = item.name || item.dataSourceName || 'Data Assessment';

    this.dialogService.openConfirmDialog(
      'Delete Data Assessment',
      `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      'Delete',
      'Cancel'
    ).subscribe(confirmed => {
      if (confirmed && runId) {
        this.deleteDataProfile(runId);
      }
    });
  }

  deleteDataProfile(runId: string): void {
    this.dataService.deleteDataProfileRun(runId).subscribe({
      next: (success: boolean) => {
        if (success) {
          console.log('Data profile deleted successfully');
          this.loadActivity();
        } else {
          console.error('Failed to delete data profile');
          this.snackBar.open('Failed to delete data assessment. Please try again.', '×', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      },
      error: (error) => {
        console.error('Error deleting data profile:', error);
        this.snackBar.open('Failed to delete data assessment. Please try again.', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  toggleLeftSidebar(): void {
    this.isLeftSidebarCollapsed = !this.isLeftSidebarCollapsed;
  }

  /**
   * Check if a project can start the pipeline (not Incomplete Setup or In Progress)
   */
  canStartPipeline(project: any): boolean {
    // Get the effective status
    let status: CleanStatus;
    if (CleanStatusHelper.isProjectIncomplete(project)) {
      status = CleanStatus.Incomplete;
    } else {
      status = project.cleanStatusId || CleanStatus.Incomplete;
    }
    
    // Allow pipeline start only if status is NOT Incomplete (99) and NOT InProgress (2)
    return status !== CleanStatus.Incomplete && status !== CleanStatus.InProgress;
  }

  /**
   * Check if a project can view outputs (Successful or Failed status)
   */
  canViewOutputs(project: any): boolean {
    // Get the effective status
    let status: CleanStatus;
    if (CleanStatusHelper.isProjectIncomplete(project)) {
      status = CleanStatus.Incomplete;
    } else {
      status = project.cleanStatusId || CleanStatus.Incomplete;
    }
    
    // Allow viewing outputs only for Successful (3) or Failed (4) status
    return status === CleanStatus.Successful || status === CleanStatus.Failed;
  }

  /**
   * Start the pipeline for a project from the activity list
   */
  onStartPipeline(event: Event, project: any): void {
    event.stopPropagation();
    
    const projectId = project.projectId || project.primaryKey;
    if (!projectId) {
      console.error('Project ID is missing');
      return;
    }

    // If we're in All Activity view and don't have complete project data, fetch it first
    if (this.selectedType === 'all' && (project.cleanStatusId === undefined || project.goalCount === undefined)) {
      this.dataService.getProject(projectId).subscribe({
        next: (fullProject: ProjectGet) => {
          // Check if the project can start pipeline
          if (!this.canStartPipeline(fullProject)) {
            this.showStartPipelineError(fullProject);
            return;
          }
          // Continue with the start pipeline logic using full project data
          this.executeStartPipeline(fullProject);
        },
        error: (error) => {
          console.error('Error fetching project details:', error);
          this.snackBar.open(error.message || 'Failed to load project details', '×', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      });
      return;
    }

    // Check validation for projects with complete data
    if (!this.canStartPipeline(project)) {
      this.showStartPipelineError(project);
      return;
    }

    // Continue with normal flow
    this.executeStartPipeline(project);
  }

  /**
   * Show appropriate error message when pipeline cannot start
   */
  private showStartPipelineError(project: any): void {
    let errorMessage = '';
    
    // Get the effective status
    let status: CleanStatus;
    if (CleanStatusHelper.isProjectIncomplete(project)) {
      status = CleanStatus.Incomplete;
      
      // Determine specific reason for incomplete status
      if (!project.dataSourceId) {
        errorMessage = 'This workflow does not have a data source configured. Please configure a data source first.';
      } else if (project.goalCount < 1) {
        errorMessage = 'This workflow does not have any goals configured. Please add at least one goal first.';
      } else if ((project.outputCount ?? 0) < 1) {
        errorMessage = 'This workflow does not have any destinations configured. Please add at least one destination first.';
      } else {
        errorMessage = 'This workflow is not fully configured. Please finish the configuration first.';
      }
    } else {
      status = project.cleanStatusId || CleanStatus.Incomplete;
      
      if (status === CleanStatus.InProgress) {
        errorMessage = 'This workflow is already in progress. Please wait for it to complete.';
      } else {
        errorMessage = 'This workflow cannot be started at this time.';
      }
    }

    this.snackBar.open(errorMessage, '×', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }

  /**
   * Execute the start pipeline logic after validation
   */
  private executeStartPipeline(project: any): void {
    const projectId = project.projectId || project.primaryKey;
    
    // First get the full project details to check if it has a data source
    if (!project.dataSourceId) {
      this.dialogService.openConfirmDialog(
        'Workflow Start Failed',
        'This project does not have a data source configured. Please configure a data source first.',
        'OK'
      ).subscribe();
      return;
    }

    // Get the data source details
    this.dataService.getDataSource(project.dataSourceId).subscribe({
      next: (dataSource: DataSourceGet) => {
        // For API Listener projects (dataSourceTypeId === 1), show the guidance dialog
        if (dataSource.dataSourceTypeId === 1) {
          this.dialogService.openDialog(StartProjectDialogComponent, {
            width: '800px',
            maxWidth: '90vw',
            data: {
              project: project,
              dataSource: dataSource
            } as StartProjectDialogData
          });
          return;
        }

        // For file-based projects, show confirmation dialog
        // Guard: block if this project is already being cleaned
        if (this.cleaningStatusService.isProjectCleaning(projectId)) {
          this.snackBar.open(
            `Cleaning is already in progress for "${project.projectName}". Please wait for it to finish.`,
            '×',
            { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] }
          );
          return;
        }

        this.dialogService.openConfirmDialog(
          'Start Workflow',
          `Start the data cleaning workflow for "${project.projectName}"? Your data will be processed according to the configured goals and sent to the specified destinations.`,
          'Start',
          'Cancel'
        ).subscribe(confirmed => {
          if (confirmed) {
            this.loadingData = true;
            this.cleaningStatusService.notifyStarted(projectId, project.projectName);

            // Show info snackbar
            this.snackBar.openFromComponent(CleaningSnackbarComponent, {
              data: {
                message: 'Starting data cleaning workflow...',
                type: 'info',
                showViewResults: false
              } as CleaningSnackbarData,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: 'info-snackbar',
              duration: 3000
            });

            // First, get the project token
            this.dataService.getProjectToken(projectId).subscribe({
              next: (projectToken) => {
                console.log('Project token obtained, starting workflow...');
                
                // Now call cleanData with the project token to get the action token
                this.dataService.cleanData(
                  projectId,
                  dataSource.dataSourceId!,
                  projectToken
                ).subscribe({
                  next: (actionToken) => {
                    console.log('Workflow started, polling for status with token:', actionToken);
                    this.cleaningStatusService.notifyProgress(projectId, project.projectName);

                    // Poll for status every 3 seconds until success (2) or failure (3)
                    this.pollingSubscription?.unsubscribe();
                    this.pollingSubscription = interval(3000).pipe(
                      switchMap(() => this.dataService.getLongRunningActionStatus(actionToken)),
                      takeWhile(status => status.status !== 2 && status.status !== 3 && status.status !== 4, true)
                    ).subscribe({
                      next: (status) => {
                        if (status.status === 2) {
                          // Success - wait one interval then fetch the result
                          setTimeout(() => {
                          this.cleaningStatusService.notifyProgress(projectId, project.projectName, undefined, undefined, 'Fetching Results');
                          this.dataService.getLongRunningActionResult(actionToken).subscribe({
                            next: (result) => {
                              console.log('Cleaning completed successfully:', result);
                              this.loadingData = false;

                              const responseData = result.cleanResult?.resultJson
                                ? JSON.parse(result.cleanResult.resultJson)
                                : null;
                              this.cleaningStatusService.notifyCompleted(projectId, project.projectName, responseData);

                              // Reload activity list to show updated status
                              this.loadActivity();
                            },
                            error: (error: any) => {
                              console.error('Error fetching cleaning result:', error);
                              this.loadingData = false;
                              this.cleaningStatusService.notifyCompleted(projectId, project.projectName);
                              this.snackBar.open('Cleaning Complete - Could not retrieve results', '×', {
                                horizontalPosition: 'center',
                                verticalPosition: 'top',
                                panelClass: ['error-snackbar']
                              });
                              this.loadActivity();
                            }
                          });
                          }, 3000);
                        } else if (status.status === 3) {
                          // Failed
                          console.error('Workflow failed:', status.errorMessage);
                          this.loadingData = false;
                          this.cleaningStatusService.notifyFailed(projectId, project.projectName);

                          this.dialogService.openConfirmDialog(
                            'Workflow Failed',
                            `The workflow failed: ${status.errorMessage || 'Unknown error'}`,
                            'OK'
                          ).subscribe();

                          this.loadActivity();
                        } else if (status.status === 4) {
                          // SaveFailed - engine succeeded but results could not be persisted
                          console.error('Workflow results could not be saved:', status.errorMessage);
                          this.loadingData = false;
                          this.cleaningStatusService.notifyFailed(projectId, project.projectName);

                          this.dialogService.openConfirmDialog(
                            'Results Not Saved',
                            'Cleaning completed but the results could not be saved. Please try again.',
                            'OK'
                          ).subscribe();

                          this.loadActivity();
                        } else {
                          // Still running - update step progress in header
                          if (status.currentStep != null && status.totalSteps != null) {
                            // Parse column name from detailsJson
                            let columnName: string | undefined;
                            if (status.detailsJson) {
                              try {
                                const details = JSON.parse(status.detailsJson);
                                columnName = details.column?.text || details.column?.source || details.column?.name || details.column || undefined;
                              } catch (e) {
                                console.warn('Failed to parse detailsJson:', e);
                              }
                            }
                            this.cleaningStatusService.notifyProgress(projectId, project.projectName, status.currentStep, status.totalSteps, undefined, status.goalName, columnName);
                          }
                        }
                        // status 0 or 1 - still running, continue polling
                      },
                      error: (error: any) => {
                        console.error('Error polling for workflow status:', error);
                        this.loadingData = false;
                        this.cleaningStatusService.notifyFailed(projectId, project.projectName);

                        this.dialogService.openConfirmDialog(
                          'Workflow Start Failed',
                          `Failed to check workflow status: ${error.message || 'Unknown error'}`,
                          'OK'
                        ).subscribe();
                      }
                    });
                  },
                  error: (error: any) => {
                    console.error('Error starting workflow:', error);
                    this.loadingData = false;
                    this.cleaningStatusService.notifyFailed(projectId, project.projectName);
                    
                    // Show error dialog
                    this.dialogService.openConfirmDialog(
                      'Workflow Start Failed',
                      `Failed to start the workflow: ${error.message || 'Unknown error'}`,
                      'OK'
                    ).subscribe();
                  }
                });
              },
              error: (error: any) => {
                console.error('Error getting project token:', error);
                this.loadingData = false;
                this.cleaningStatusService.notifyFailed(projectId, project.projectName);
                this.dialogService.openConfirmDialog(
                  'Workflow Start Failed',
                  `Failed to get project token: ${error.message || 'Unknown error'}`,
                  'OK'
                ).subscribe();
              }
            });
          }
        });
      },
      error: (error: any) => {
        console.error('Error getting data source:', error);
        this.dialogService.openConfirmDialog(
          'Workflow Start Failed',
          `Failed to get project data source: ${error.message || 'Unknown error'}`,
          'OK'
        ).subscribe();
      }
    });
  }

  applyTableFilter(value: string) {
    this.searchQuery = value?.trim().toLowerCase();
    this.dataSource.filter = this.buildFilterString();
  }

  clearSearch() {
    this.searchQuery = '';
    this.dataSource.filter = this.buildFilterString();
  }

  setSubFilter(value: string): void {
    this.subFilter = value;
    this.dataSource.filter = this.buildFilterString();
  }

  getFilterButtonTooltip(): string {
    if (this.selectedType === 'dataprofile' || this.selectedType === 'anomalydetection') {
      return 'No filters available';
    }
    if (this.subFilter === 'all') return 'Filter';

    const allLabels: Record<string, string> = {
      project: 'Cleaning Workflows',
      dataprofile: 'Data Assessments', anomalydetection: 'Anomaly Detection'
    };
    const projectLabels: Record<string, string> = {
      '1': 'Not Started', '2': 'In Progress', '3': 'Successful', '4': 'Failed', '99': 'Incomplete Setup'
    };

    let label: string | undefined;
    if (this.selectedType === 'all') label = allLabels[this.subFilter];
    else if (this.selectedType === 'project') label = projectLabels[this.subFilter];

    return `Filter: ${label ?? this.subFilter}`;
  }

  private buildFilterString(): string {
    return `${this.searchQuery ?? ''}\x1f${this.subFilter}`;
  }

  private updateDataSourceFilterPredicate() {
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const sepIdx = filter.indexOf('\x1f');
      const searchPart = sepIdx >= 0 ? filter.slice(0, sepIdx) : filter;
      const sf = sepIdx >= 0 ? filter.slice(sepIdx + 1) : 'all';

      const matchesSearch = !searchPart || [data.name, data.description, data.dataSourceName, data.type]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(searchPart);

      if (!matchesSearch) return false;

      // Top-level type filter
      const type = this.currentTypeFilter;
      if (type === 'project' && !this.isProject(data)) return false;
      if (type === 'dataprofile' && !this.isDataProfile(data)) return false;
      if (type === 'anomalydetection' && !this.isAnomalyDetection(data)) return false;

      if (sf === 'all') return true;

      // Sub-filter: All Activity view → filter by item type
      if (type === 'all') {
        if (sf === 'project') return this.isProject(data);
        if (sf === 'dataprofile') return this.isDataProfile(data);
        if (sf === 'anomalydetection') return this.isAnomalyDetection(data);
      }

      // Sub-filter: Projects view → filter by effective clean status
      if (type === 'project') {
        const effectiveStatus = CleanStatusHelper.isProjectIncomplete(data)
          ? CleanStatus.Incomplete
          : (data.cleanStatusId || CleanStatus.Incomplete);
        return String(effectiveStatus) === sf;
      }

      return true;
    };
  }
}
